import { Banner } from "@shopify/polaris";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { syncBilling } from "./api/billing";
import { InstallSurveyModal } from "./components/InstallSurveyModal";
import { Layout } from "./components/Layout";
import { applyAuthoritativeSubscription } from "./lib/planAuthority";
import { Bom } from "./pages/Bom";
import { CashflowCalendar } from "./pages/CashflowCalendar";
import { CompanyProfileSettings } from "./pages/CompanyProfileSettings";
import { Contact } from "./pages/Contact";
import { Dashboard } from "./pages/Dashboard";
import { Help } from "./pages/Help";
import { Home } from "./pages/Home";
import { InventoryAnalytics } from "./pages/InventoryAnalytics";
import { InventoryReconciliation } from "./pages/InventoryReconciliation";
import { PurchaseOrderEmailReconciliation } from "./pages/PurchaseOrderEmailReconciliation";
import { Reports } from "./pages/Reports";
import { NotificationSettings } from "./pages/NotificationSettings";
import { Plans } from "./pages/Plans";
import { InventoryAdjustments } from "./pages/InventoryAdjustments";
import { InventoryTransfers } from "./pages/InventoryTransfers";
import { Stocktakes } from "./pages/Stocktakes";
import { PlacedOrders } from "./pages/PlacedOrders";
import { PurchaseOrders } from "./pages/PurchaseOrders";
import { ReorderPolicies } from "./pages/ReorderPolicies";
import { SeasonalRules } from "./pages/SeasonalRules";
import { StockyData } from "./pages/StockyData";
import { StockyCsvImport } from "./pages/StockyCsvImport";
import { TaxRateSettings } from "./pages/TaxRateSettings";
import { Suppliers } from "./pages/Suppliers";

import { t } from "./lib/i18n";

type BillingSyncState =
  | { status: "idle" }
  | { status: "syncing" }
  | { status: "done" }
  | { status: "error"; message: string };

type BillingSyncController = BillingSyncState & { retry: () => void };

// Shopify can redirect back before `activeSubscriptions` exposes the newly
// approved subscription. Keep this deliberately short: enough to bridge normal
// propagation, without leaving the merchant staring at an indefinite spinner.
export const BILLING_RETURN_RETRY_DELAYS_MS = [0, 250, 750, 1_500] as const;

const BILLING_NOT_ACTIVE_MESSAGE =
  "Shopify でプランの有効化をまだ確認できません。少し待ってから再試行してください。";

/**
 * Keep a URL-derived pathname inside this SPA before passing it back to
 * React Router.  All application routes are ASCII slugs (plus numeric IDs),
 * so percent escapes, backslashes and protocol-relative `//` paths are never
 * legitimate here.  Rejecting them also closes open-redirect parsing gaps in
 * vulnerable React Router v6 releases without guessing how many times an
 * attacker-controlled path might be decoded.
 */
export function safeInternalPathname(pathname: string): string {
  if (pathname.startsWith("//") || !/^\/[A-Za-z0-9/_-]*$/.test(pathname)) {
    return "/";
  }
  return pathname;
}

export function retiredStockyCopyTarget(
  search: string,
  pathname = "/stocky-csv",
) {
  const params = new URLSearchParams(search);
  params.delete("profile");
  const nextSearch = params.toString();
  return {
    pathname,
    search: nextSearch ? `?${nextSearch}` : "",
  };
}

function RetiredStockyCopyRedirect({ pathname }: { pathname?: string }) {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={retiredStockyCopyTarget(location.search, pathname)}
    />
  );
}

/**
 * Top-level effect that reconciles billing entitlements with Shopify.
 *
 * GraphQL app subscriptions auto-activate on approval (no separate activation
 * step), so we converge local state via POST /billing/sync in two cases:
 *
 * 1. Return from Shopify's approval page (`?billing_pending=1&charge_id=...`):
 *    a VISIBLE sync — show a banner, invalidate ["me"], then strip the params
 *    so a refresh doesn't re-run it. Errors are surfaced (the merchant waits).
 * 2. Normal app load: a SILENT best-effort backstop for a missed
 *    `app_subscriptions/update` webhook. Runs once; errors are swallowed (the
 *    webhook or the next load reconciles) so we never flash a red banner on a
 *    transient backend blip during ordinary navigation.
 */
export function useBillingSync(): BillingSyncController {
  const [state, setState] = useState<BillingSyncState>({ status: "idle" });
  const [retryGeneration, setRetryGeneration] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const didBackstopRef = useRef(false);
  const runSequenceRef = useRef(0);
  const latestLocationRef = useRef(location);
  latestLocationRef.current = location;

  const currentParams = new URLSearchParams(location.search);
  const billingReturnChargeId =
    currentParams.get("billing_pending") === "1"
      ? (currentParams.get("charge_id") ?? "")
      : null;
  const billingReturnKey =
    billingReturnChargeId === null ? null : `billing:${billingReturnChargeId}`;

  const retry = useCallback(() => {
    const params = new URLSearchParams(latestLocationRef.current.search);
    if (params.get("billing_pending") !== "1") return;
    setRetryGeneration((value) => value + 1);
  }, []);

  useEffect(() => {
    const sequence = ++runSequenceRef.current;
    let timerId: number | undefined;
    const isCurrentRun = () => runSequenceRef.current === sequence;
    const schedule = (callback: () => void, delayMs: number) => {
      timerId = window.setTimeout(() => {
        timerId = undefined;
        callback();
      }, delayMs);
    };

    if (billingReturnKey !== null) {
      const runAttempt = async (attempt: number): Promise<void> => {
        didBackstopRef.current = true;
        setState({ status: "syncing" });

        try {
          const result = await syncBilling();
          if (!isCurrentRun()) return;

          // An existing paid plan can remain visible briefly while Shopify
          // propagates a newly-approved plan change.  "Some ACTIVE charge" is
          // therefore not proof that this callback converged: require the exact
          // charge id whose approval URL returned the merchant here.
          if (
            result.active === true &&
            result.status === "active" &&
            result.shopify_charge_id === billingReturnChargeId
          ) {
            applyAuthoritativeSubscription(queryClient, result);
            setState({ status: "done" });
            void queryClient.invalidateQueries({ queryKey: ["me"] });

            // Strip only billing callback params, and do it on the latest route.
            // A merchant can navigate while Shopify state is propagating; using
            // the route captured by the first attempt would jump them backwards.
            const latestLocation = latestLocationRef.current;
            const params = new URLSearchParams(latestLocation.search);
            params.delete("billing_pending");
            params.delete("charge_id");
            const qs = params.toString();
            navigate(
              {
                pathname: safeInternalPathname(latestLocation.pathname),
                search: qs ? `?${qs}` : "",
              },
              { replace: true },
            );
            return;
          }

          const nextAttempt = attempt + 1;
          if (nextAttempt < BILLING_RETURN_RETRY_DELAYS_MS.length) {
            schedule(
              () => void runAttempt(nextAttempt),
              BILLING_RETURN_RETRY_DELAYS_MS[nextAttempt],
            );
            return;
          }
          setState({ status: "error", message: t(BILLING_NOT_ACTIVE_MESSAGE) });
        } catch (error) {
          if (!isCurrentRun()) return;
          const nextAttempt = attempt + 1;
          if (nextAttempt < BILLING_RETURN_RETRY_DELAYS_MS.length) {
            schedule(
              () => void runAttempt(nextAttempt),
              BILLING_RETURN_RETRY_DELAYS_MS[nextAttempt],
            );
            return;
          }
          setState({
            status: "error",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      };

      // Deferring the first request by one task is intentional. React StrictMode
      // replays effects in development; cleanup cancels the first scheduled task,
      // so the billing mutation is still pulled exactly once.
      schedule(() => void runAttempt(0), BILLING_RETURN_RETRY_DELAYS_MS[0]);
    } else if (!didBackstopRef.current) {
      // Normal-load backstop remains silent and best-effort. It is also deferred
      // for the same StrictMode reason; mark it started only when the timer fires.
      schedule(() => {
        didBackstopRef.current = true;
        syncBilling()
          .then((result) => {
            if (isCurrentRun()) {
              applyAuthoritativeSubscription(queryClient, result);
              void queryClient.invalidateQueries({ queryKey: ["me"] });
            }
          })
          .catch(() => {
            // Intentional: the webhook (or the next load's backstop) reconciles.
          });
      }, 0);
    }

    return () => {
      if (timerId !== undefined) window.clearTimeout(timerId);
      if (runSequenceRef.current === sequence) runSequenceRef.current += 1;
    };
  }, [billingReturnKey, navigate, queryClient, retryGeneration]);

  return { ...state, retry };
}

export function App() {
  const billingSync = useBillingSync();

  return (
    <Layout>
      <InstallSurveyModal />
      {billingSync.status === "syncing" && (
        <Banner tone="info">{t("プランを反映しています…")}</Banner>
      )}
      {billingSync.status === "done" && (
        <Banner tone="success">{t("プランが有効になりました。")}</Banner>
      )}
      {billingSync.status === "error" && (
        <Banner
          tone="critical"
          action={{ content: t("再試行"), onAction: billingSync.retry }}
        >
          {t("プランの反映に失敗しました:")} {billingSync.message}
        </Banner>
      )}

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/migrate" element={<RetiredStockyCopyRedirect />} />
        <Route
          path="/stocky-copy/run"
          element={<RetiredStockyCopyRedirect pathname="/stocky-data" />}
        />
        <Route
          path="/cws-stocky-copy-review"
          element={<RetiredStockyCopyRedirect pathname="/stocky-data" />}
        />
        <Route path="/stocky-data" element={<StockyData />} />
        <Route path="/stocky-data/:resource" element={<StockyData />} />
        <Route
          path="/stocky-data/:resource/:recordId"
          element={<StockyData />}
        />
        <Route path="/stocky-csv/*" element={<StockyCsvImport />} />
        <Route path="/settings" element={<NotificationSettings />} />
        <Route path="/company-profile" element={<CompanyProfileSettings />} />
        <Route path="/tax-rates" element={<TaxRateSettings />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/purchase-orders" element={<PurchaseOrders />} />
        <Route
          path="/purchase-order-email-reconciliation"
          element={<PurchaseOrderEmailReconciliation />}
        />
        <Route
          path="/purchase-order-email-reconciliation/:deliveryId"
          element={<PurchaseOrderEmailReconciliation />}
        />
        <Route path="/placed-orders" element={<PlacedOrders />} />
        <Route
          path="/inventory-adjustments"
          element={<InventoryAdjustments />}
        />
        <Route path="/stocktakes" element={<Stocktakes />} />
        <Route path="/inventory-transfers" element={<InventoryTransfers />} />
        <Route
          path="/inventory-reconciliation"
          element={<InventoryReconciliation />}
        />
        <Route
          path="/inventory-reconciliation/:operationId"
          element={<InventoryReconciliation />}
        />
        <Route path="/cashflow-calendar" element={<CashflowCalendar />} />
        <Route path="/inventory-analytics" element={<InventoryAnalytics />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/bom" element={<Bom />} />
        <Route path="/reorder-policies" element={<ReorderPolicies />} />
        <Route path="/seasonal-rules" element={<SeasonalRules />} />
        <Route path="/help" element={<Help />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </Layout>
  );
}
